import { PlusIcon } from "@heroicons/react/24/outline";
import { resolveLanguageKey } from "@packages/core/language/language.ts";
import { FilterChipButton } from "@packages/core-client/collection/FilterChip.tsx";
import { type FilterKind, FilterKinds } from "@domains/reviews/client/components/reviewee-filters/filterKinds.ts";
import { useCachedManagerNames, useCachedSegmentNames } from "@domains/reviews/client/components/reviewee-filters/useCachedOptionNames.ts";
import { reviewCyclesI18NextInstanceKey } from "@domains/reviews/client/i18n/i18next.ts";
import { DepartmentFilterPanel } from "@domains/reviews/client/components/reviewee-filters/DepartmentFilterPanel.tsx";
import { DirectManagersFilterPanel } from "@domains/reviews/client/components/reviewee-filters/DirectManagersFilterPanel.tsx";
import { FilterChipPopover } from "@domains/reviews/client/components/reviewee-filters/FilterChipPopover.tsx";
import { FilterKindList } from "@domains/reviews/client/components/reviewee-filters/FilterKindList.tsx";
import { GenderFilterPanel } from "@domains/reviews/client/components/reviewee-filters/GenderFilterPanel.tsx";
import { JobTitleFilterPanel } from "@domains/reviews/client/components/reviewee-filters/JobTitleFilterPanel.tsx";
import { ReportingLineFilterPanel } from "@domains/reviews/client/components/reviewee-filters/ReportingLineFilterPanel.tsx";
import { RoleLevelFilterPanel } from "@domains/reviews/client/components/reviewee-filters/RoleLevelFilterPanel.tsx";
import { SegmentFilterPanel } from "@domains/reviews/client/components/reviewee-filters/SegmentFilterPanel.tsx";
import { TenureFilterPanel } from "@domains/reviews/client/components/reviewee-filters/TenureFilterPanel.tsx";
import { TrackFilterPanel } from "@domains/reviews/client/components/reviewee-filters/TrackFilterPanel.tsx";
import { WorkLocationFilterPanel } from "@domains/reviews/client/components/reviewee-filters/WorkLocationFilterPanel.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TENURE_BUCKET_LABELS } from "#shared/columnLabels.ts";
import type { ComparisonInput } from "#shared/comparisonSelection.ts";
import { type TenureBucketKey, toTenureBucketKey } from "@domains/reviews/shared/reviewee-filters/tenureBuckets.ts";
import { toIsReportingManager, toTrackOption, type TrackOption } from "@domains/reviews/shared/reviewee-filters/trackOptions.ts";
import { Popover, PopoverContent } from "@/components/ui/popover.tsx";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
type AttributeKind = "department" | "gender" | "workLocation" | "roleLevel" | "jobTitle";

const ATTRIBUTE_FIELD: Record<AttributeKind, "departments" | "genders" | "workLocations" | "jobLevels" | "jobTitles"> = {
    department: "departments",
    gender: "genders",
    workLocation: "workLocations",
    roleLevel: "jobLevels",
    jobTitle: "jobTitles"
};

interface FilterChipDescriptor {
    value: string;
    renderPanel: (close: () => void) => ReactNode;
}

export interface ComparisonFilterChipsProps {
    selection: ComparisonInput;
    onChange: (selection: ComparisonInput) => void;
}

const NO_ADDED_KINDS: ReadonlySet<FilterKind> = new Set();
const NO_VALUES: string[] = [];

export function ComparisonFilterChips({ selection, onChange }: ComparisonFilterChipsProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ComparisonFilterChips", { i18n: i18nextInstance });
    const revieweeFiltersI18n = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t: tOptions } = useTranslation("TrackOptions", { i18n: revieweeFiltersI18n });
    const { t: tKinds } = useTranslation("FilterKinds", { i18n: revieweeFiltersI18n });
    const managerNames = useCachedManagerNames();
    const segmentNames = useCachedSegmentNames();
    const language = resolveLanguageKey(i18nextInstance.language);

    const [addedKinds, setAddedKinds] = useState<ReadonlySet<FilterKind>>(NO_ADDED_KINDS);

    const valuesOf = (kind: AttributeKind) => selection[ATTRIBUTE_FIELD[kind]] ?? NO_VALUES;

    const isSet = (kind: FilterKind) => {
        if (kind === "directManagers") {
            return (selection.managerIds?.length ?? 0) > 0;
        }
        if (kind === "reportingLine") {
            return (selection.reportingLineIds?.length ?? 0) > 0;
        }
        if (kind === "segment") {
            return (selection.segmentIds?.length ?? 0) > 0;
        }
        if (kind === "tenure") {
            return Boolean(selection.tenure ?? selection.hireDateMin ?? selection.hireDateMax);
        }
        if (kind === "track") {
            return selection.isReportingManager !== undefined;
        }

        return valuesOf(kind).length > 0;
    };

    const isVisible = (kind: FilterKind) => isSet(kind) || addedKinds.has(kind);

    const forget = useCallback((kind: FilterKind) => {
        setAddedKinds(current => {
            const next = new Set(current);
            next.delete(kind);

            return next;
        });
    }, []);

    const write = useCallback((patch: Partial<ComparisonInput>) => onChange({ ...selection, ...patch }), [onChange, selection]);

    const setManagers = useCallback((managerIds: string[]) => write({ managerIds: managerIds.length > 0 ? managerIds : undefined }), [write]);
    const setReportingLines = useCallback((reportingLineIds: string[]) => write({ reportingLineIds: reportingLineIds.length > 0 ? reportingLineIds : undefined }), [write]);
    const setSegments = useCallback((segmentIds: string[]) => write({ segmentIds: segmentIds.length > 0 ? segmentIds : undefined }), [write]);
    const setDepartments = useCallback((departments: string[]) => write({ departments: departments.length > 0 ? departments : undefined }), [write]);
    const setGenders = useCallback((genders: string[]) => write({ genders: genders.length > 0 ? genders : undefined }), [write]);
    const setWorkLocations = useCallback((workLocations: string[]) => write({ workLocations: workLocations.length > 0 ? workLocations : undefined }), [write]);
    const setJobLevels = useCallback((jobLevels: string[]) => write({ jobLevels: jobLevels.length > 0 ? jobLevels : undefined }), [write]);
    const setJobTitles = useCallback((jobTitles: string[]) => write({ jobTitles: jobTitles.length > 0 ? jobTitles : undefined }), [write]);

    const setTenure = useCallback((buckets: TenureBucketKey[]) => write({ tenure: buckets[0] ?? undefined, hireDateMin: undefined, hireDateMax: undefined }), [write]);

    const setTrack = useCallback(
        (tracks: TrackOption[]) => {
            setAddedKinds(current => new Set(current).add("track"));
            write({ isReportingManager: toIsReportingManager(tracks[0] ?? null) });
        },
        [write]
    );

    const clearKind = useCallback(
        (kind: FilterKind) => {
            forget(kind);

            if (kind === "directManagers") {
                write({ managerIds: undefined });
            } else if (kind === "reportingLine") {
                write({ reportingLineIds: undefined });
            } else if (kind === "segment") {
                write({ segmentIds: undefined });
            } else if (kind === "tenure") {
                write({ tenure: undefined, hireDateMin: undefined, hireDateMax: undefined });
            } else if (kind === "track") {
                write({ isReportingManager: undefined });
            } else {
                write({ [ATTRIBUTE_FIELD[kind]]: undefined });
            }
        },
        [forget, write]
    );

    const appliedKinds = new Set(FilterKinds.filter(isVisible));

    const managerIds = selection.managerIds ?? NO_VALUES;
    const reportingLineIds = selection.reportingLineIds ?? NO_VALUES;

    const personValue = (ids: string[]) => {
        if (ids.length === 0) {
            return t("anyValue");
        }

        return (ids.length === 1 ? managerNames.get(ids[0]) : undefined) ?? t("valueCount", { count: ids.length });
    };

    const directManagersValue = personValue(managerIds);
    const reportingLineValue = personValue(reportingLineIds);

    const segmentIds = selection.segmentIds ?? NO_VALUES;

    const segmentValue = segmentIds.length === 0 ? t("anyValue") : ((segmentIds.length === 1 ? segmentNames.get(segmentIds[0]) : undefined) ?? t("valueCount", { count: segmentIds.length }));

    const tenureBucket = selection.tenure ?? toTenureBucketKey(selection, new Date());
    const tenureValue = (tenureBucket && TENURE_BUCKET_LABELS[tenureBucket]?.[language]) ?? (isSet("tenure") ? t("customValue") : t("anyValue"));
    const tenureBuckets = tenureBucket ? [tenureBucket] : [];

    const track = toTrackOption(selection.isReportingManager);
    const trackValue = track ? tOptions(track) : t("anyValue");
    const tracks = track ? [track] : [];

    const attributeValue = (kind: AttributeKind) => {
        const values = valuesOf(kind);

        if (values.length === 0) {
            return t("anyValue");
        }

        return values.length === 1 ? values[0] : t("valueCount", { count: values.length });
    };

    const chips: Record<FilterKind, FilterChipDescriptor> = {
        directManagers: { value: directManagersValue, renderPanel: close => <DirectManagersFilterPanel selectedIds={managerIds} onSelectionChange={setManagers} onBack={close} /> },
        reportingLine: { value: reportingLineValue, renderPanel: close => <ReportingLineFilterPanel selectedIds={reportingLineIds} onSelectionChange={setReportingLines} onBack={close} /> },
        segment: { value: segmentValue, renderPanel: close => <SegmentFilterPanel selectedIds={segmentIds} onSelectionChange={setSegments} onBack={close} /> },
        department: { value: attributeValue("department"), renderPanel: close => <DepartmentFilterPanel selectedValues={valuesOf("department")} onSelectionChange={setDepartments} onBack={close} /> },
        gender: { value: attributeValue("gender"), renderPanel: close => <GenderFilterPanel selectedValues={valuesOf("gender")} onSelectionChange={setGenders} onBack={close} /> },
        workLocation: { value: attributeValue("workLocation"), renderPanel: close => <WorkLocationFilterPanel selectedValues={valuesOf("workLocation")} onSelectionChange={setWorkLocations} onBack={close} /> },
        roleLevel: { value: attributeValue("roleLevel"), renderPanel: close => <RoleLevelFilterPanel selectedValues={valuesOf("roleLevel")} onSelectionChange={setJobLevels} onBack={close} /> },
        jobTitle: { value: attributeValue("jobTitle"), renderPanel: close => <JobTitleFilterPanel selectedValues={valuesOf("jobTitle")} onSelectionChange={setJobTitles} onBack={close} /> },
        tenure: { value: tenureValue, renderPanel: close => <TenureFilterPanel selectedBuckets={tenureBuckets} onSelectionChange={setTenure} onBack={close} /> },
        track: { value: trackValue, renderPanel: close => <TrackFilterPanel selectedTracks={tracks} onSelectionChange={setTrack} onBack={close} /> }
    };

    return (
        <>
            {FilterKinds.filter(isVisible).map(kind => (
                <FilterChipPopover key={kind} kind={kind} label={tKinds(kind)} value={chips[kind].value} ariaLabel={t(`${kind}.ariaLabel`, { value: chips[kind].value })} onRemove={clearKind} removeLabel={t("remove")}>
                    {chips[kind].renderPanel}
                </FilterChipPopover>
            ))}

            <AddFilterMenu appliedKinds={appliedKinds} chips={chips} />
        </>
    );
}

interface AddFilterMenuProps {
    appliedKinds: ReadonlySet<FilterKind>;
    chips: Record<FilterKind, FilterChipDescriptor>;
}

function AddFilterMenu({ appliedKinds, chips }: AddFilterMenuProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ComparisonFilterChips", { i18n: i18nextInstance });
    const revieweeFiltersI18n = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t: tKinds } = useTranslation("FilterKinds", { i18n: revieweeFiltersI18n });

    const [isOpen, setIsOpen] = useState(false);
    const [openKind, setOpenKind] = useState<FilterKind | null>(null);

    const handleOpenChange = useCallback((nextOpen: boolean) => {
        setIsOpen(nextOpen);

        if (!nextOpen) {
            setOpenKind(null);
        }
    }, []);

    const handleBackToList = useCallback(() => setOpenKind(null), []);

    return (
        <Popover isOpen={isOpen} onOpenChange={handleOpenChange}>
            <FilterChipButton className="text-muted-fg">
                <PlusIcon className="size-4" aria-hidden="true" />
                {t("addFilter")}
            </FilterChipButton>
            <PopoverContent placement="bottom start" aria-label={t("addFilter")} className="bg-bg p-3 [&_[data-slot=popover-inner]]:overflow-x-hidden">
                {openKind ? chips[openKind].renderPanel(handleBackToList) : <FilterKindList ariaLabel={t("addFilter")} kinds={FilterKinds} appliedKinds={appliedKinds} getKindLabel={tKinds} appliedLabel={tKinds("applied")} onSelect={setOpenKind} className="w-56" />}
            </PopoverContent>
        </Popover>
    );
}
